import React, { createContext, useContext, useLayoutEffect, useMemo, useState } from 'react';

const ThemeContext = createContext({
  theme: 'dark',
  toggleTheme: () => {},
  setTheme: () => {}
});

const STORAGE_KEY = 'guhso-ui-theme';

export const ThemeProvider = ({ defaultTheme = 'dark', children }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') {
      return defaultTheme;
    }
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === 'dark' || stored === 'light') return stored;
    } catch { /* Storage may be unavailable in private browsing. */ }
    return defaultTheme;
  });

  useLayoutEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
      document.documentElement.style.colorScheme = theme;
    }
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(STORAGE_KEY, theme); } catch { /* Keep the in-memory preference. */ }
    }
  }, [theme]);

  const setTheme = (value) => {
    if (value === 'dark' || value === 'light') setThemeState(value);
  };

  const toggleTheme = () => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggleTheme
    }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);
